import { Injectable } from '@angular/core';
import { FileOpener } from '@capacitor-community/file-opener';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import jsPDF from 'jspdf';
import { html2PDF } from '../lib/jsPDF-html2canvas/js-pdf';
import { Client } from '../models/client.model';

async function convertBlobToBase64(blob: Blob): Promise<string | ArrayBuffer> {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

@Injectable({
  providedIn: 'root',
})
export class PrintService {

  private addFont(pdf: jsPDF) {
    pdf.addFont(
      'https://firebasestorage.googleapis.com/v0/b/kiira-app-prod.appspot.com/o/fonts%2FMontserrat%2FMontserrat-Regular.ttf?alt=media&token=9c1ae89f-2b3b-4d29-a82c-9d5893417da9',
      'Montserrat', 'normal');
    pdf.addFont(
      'https://firebasestorage.googleapis.com/v0/b/kiira-app-prod.appspot.com/o/fonts%2FMontserrat%2FMontserrat-Bold.ttf?alt=media&token=3a7a5c75-da5e-47f3-8b7f-0cc9ff492ff6',
      'Montserrat', 'bold');
    pdf.addFont(
      'https://firebasestorage.googleapis.com/v0/b/kiira-app-prod.appspot.com/o/fonts%2FMontserrat%2FMontserrat-Italic.ttf?alt=media&token=5615a915-4bca-4110-9a12-c63e4899966b',
      'Montserrat', 'italic');
    pdf.setFont('Montserrat');
    return pdf;
  }

  private async openFile(filename: string, data: Blob) {
    const base64 = await convertBlobToBase64(data) as string;
    const writeResult = await Filesystem.writeFile({
      path: `kiira/${filename}`,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });
    const filePath = writeResult.uri;
    FileOpener.open({ filePath, contentType: 'application/pdf' });
  }

  async printDoc(
    name: string,
    htmlElement: HTMLElement | HTMLElement[],
    css?: string,
    options?: {
      orientation?: 'landscape' | 'portrait'; scale?: number; output?: boolean;
      margins?: { top: number; right: number; bottom: number; left: number };
    },
    client?: Client,
    header?: string,
  ) {
    let style: HTMLStyleElement;

    if (css != null) {
      style = document.createElement('style');
      style.prepend(css);
      if (Array.isArray(htmlElement)) {
        for (const el of htmlElement) {
          el.prepend(style);
        }
      } else {
        htmlElement.prepend(style);
      }
    }

    return html2PDF(htmlElement, {
      jsPDF: {
        format: 'a4',
        orientation: options?.orientation ?? 'landscape',
      },
      margin: options?.margins ?? {
        top: 16,
        right: 16,
        bottom: 16,
        left: 16,
      },
      html2canvas: {
        scale: options?.scale ?? 1,
        useCORS: true,
      },
      success: () => { /** empty */ },
    }).then((pdf) => {
      this.addFont(pdf);
      pdf.setFontSize(10);
      const headerY = 50;
      const footerY = 20;
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        const x = pdf.internal.pageSize.getWidth();
        pdf.setPage(i);
        pdf.text(`${i} (${pageCount})`, x - 45, headerY);
        if (header) {
          if (client?.logo) {
            const img = new Image();
            img.src = client.logo;
            const imgProps = pdf.getImageProperties(img);
            const width = 100;
            const height = (imgProps.height * width) / imgProps.width;
            pdf.addImage(img, 14, headerY - 13, width, height);
          }
          pdf.text(`${header}`, x / 2, headerY);
        }
        if (client) {
          const y = pdf.internal.pageSize.getHeight();
          pdf.text(`${client.name}`, 16, y - footerY);
          pdf.text(`Y-tunnus ${client.businessId}`, x - 102, y - footerY);
        }
      }

      const blob = pdf.output('blob');
      style?.remove();
      if (options?.output) {
        return blob;
      }
      if (Capacitor.isNativePlatform()) {
        this.openFile(`${name}.pdf`, blob);
      } else {
        pdf.save(`${name}.pdf`);
      }
      return blob;
    }, (error) => console.error(error));
  }
}
